import React from 'react';
import {
  Settings,
  Palette,
  Bot,
  Cloud,
  Database,
  TestTube,
  LucideIcon,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';

interface SkillsSectionProps {
  className?: string;
}

interface Skill {
  name: string;
  level: 'Expert' | 'Advanced' | 'Intermediate' | 'Learning';
  category: string;
}

interface SkillCategory {
  title: string;
  icon: LucideIcon;
  description: string;
  skills: Skill[];
}

const SkillsSection: React.FC<SkillsSectionProps> = ({ className }) => {
  const skillCategories: SkillCategory[] = [
    {
      title: 'Backend Development',
      icon: Settings,
      description: 'Server-side technologies and frameworks',
      skills: [
        { name: 'Java', level: 'Expert', category: 'Backend' },
        { name: 'Spring Boot', level: 'Expert', category: 'Backend' },
        { name: 'Kotlin', level: 'Advanced', category: 'Backend' },
        { name: 'Python', level: 'Advanced', category: 'Backend' },
        { name: 'Go', level: 'Intermediate', category: 'Backend' },
        { name: 'RESTful APIs', level: 'Expert', category: 'Backend' },
        { name: 'GraphQL', level: 'Advanced', category: 'Backend' },
      ],
    },
    {
      title: 'Frontend Development',
      icon: Palette,
      description: 'Modern web technologies and frameworks',
      skills: [
        { name: 'React.js', level: 'Expert', category: 'Frontend' },
        { name: 'TypeScript', level: 'Expert', category: 'Frontend' },
        { name: 'JavaScript', level: 'Expert', category: 'Frontend' },
        { name: 'HTML5/CSS3', level: 'Expert', category: 'Frontend' },
        { name: 'Tailwind CSS', level: 'Advanced', category: 'Frontend' },
        { name: 'Next.js', level: 'Advanced', category: 'Frontend' },
      ],
    },
    {
      title: 'AI & Machine Learning',
      icon: Bot,
      description: 'Artificial Intelligence and ML technologies',
      skills: [
        { name: 'Python ML Stack', level: 'Advanced', category: 'AI/ML' },
        { name: 'TensorFlow', level: 'Intermediate', category: 'AI/ML' },
        { name: 'PyTorch', level: 'Intermediate', category: 'AI/ML' },
        { name: 'LangChain', level: 'Advanced', category: 'AI/ML' },
        { name: 'OpenAI APIs', level: 'Advanced', category: 'AI/ML' },
        { name: 'Hugging Face', level: 'Intermediate', category: 'AI/ML' },
        { name: 'Vector Databases', level: 'Intermediate', category: 'AI/ML' },
        { name: 'RAG Systems', level: 'Advanced', category: 'AI/ML' },
        { name: 'Prompt Engineering', level: 'Advanced', category: 'AI/ML' },
      ],
    },
    {
      title: 'DevOps & Cloud',
      icon: Cloud,
      description: 'Cloud platforms and deployment technologies',
      skills: [
        { name: 'AWS', level: 'Advanced', category: 'DevOps' },
        { name: 'Docker', level: 'Advanced', category: 'DevOps' },
        { name: 'Kubernetes', level: 'Advanced', category: 'DevOps' },
        { name: 'CI/CD', level: 'Expert', category: 'DevOps' },
        { name: 'Airflow', level: 'Advanced', category: 'DevOps' },
        { name: 'Terraform', level: 'Intermediate', category: 'DevOps' },
      ],
    },
    {
      title: 'Databases',
      icon: Database,
      description: 'Data storage and management systems',
      skills: [
        { name: 'PostgreSQL', level: 'Expert', category: 'Database' },
        { name: 'DynamoDB', level: 'Advanced', category: 'Database' },
        { name: 'MongoDB', level: 'Intermediate', category: 'Database' },
        { name: 'Redis', level: 'Advanced', category: 'Database' },
        { name: 'Elasticsearch', level: 'Intermediate', category: 'Database' },
      ],
    },
    {
      title: 'Testing & Quality',
      icon: TestTube,
      description: 'Testing frameworks and quality assurance',
      skills: [
        { name: 'TDD', level: 'Expert', category: 'Testing' },
        { name: 'React Testing Library', level: 'Expert', category: 'Testing' },
        { name: 'RESTAssured', level: 'Advanced', category: 'Testing' },
        { name: 'Cypress', level: 'Advanced', category: 'Testing' },
        { name: 'Jest', level: 'Advanced', category: 'Testing' },
        { name: 'JUnit', level: 'Expert', category: 'Testing' },
      ],
    },
  ];

  const getLevelColor = (level: Skill['level']): string => {
    switch (level) {
      case 'Expert':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Advanced':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Intermediate':
        return 'bg-primary/10 text-primary border-primary/20';
      case 'Learning':
        return 'bg-muted text-muted-foreground border-muted';
      default:
        return 'bg-muted text-muted-foreground border-muted';
    }
  };

  const getLevelDots = (level: Skill['level']): number => {
    switch (level) {
      case 'Expert':
        return 5;
      case 'Advanced':
        return 4;
      case 'Intermediate':
        return 3;
      case 'Learning':
        return 2;
      default:
        return 1;
    }
  };

  return (
    <section id='skills' className={cn('py-20 bg-background', className)}>
      <div className='container mx-auto px-4'>
        {/* Section Header */}
        <div className='text-center mb-16'>
          <h2 className='text-3xl sm:text-4xl font-bold text-foreground mb-4'>
            Technical Skills
          </h2>
          <p className='text-lg text-muted-foreground max-w-3xl mx-auto'>
            A comprehensive toolkit spanning full-stack development, AI/ML
            technologies, and modern DevOps practices
          </p>
        </div>

        {/* Skills Grid */}
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'>
          {skillCategories.map((category, categoryIndex) => {
            const IconComponent = category.icon;
            return (
              <Card
                key={categoryIndex}
                className='hover:shadow-lg transition-all duration-300 hover:-translate-y-1'
              >
                <CardHeader className='pb-4'>
                  <div className='flex items-center gap-3 mb-2'>
                    <IconComponent className='w-6 h-6 text-primary' />
                    <CardTitle className='text-lg'>{category.title}</CardTitle>
                  </div>
                  <p className='text-sm text-muted-foreground'>
                    {category.description}
                  </p>
                </CardHeader>
                <CardContent className='space-y-3'>
                  {category.skills.map((skill, skillIndex) => (
                    <div
                      key={skillIndex}
                      className='flex items-center justify-between'
                    >
                      <div className='flex items-center gap-2'>
                        <span className='text-sm font-medium text-foreground'>
                          {skill.name}
                        </span>
                        <div className='flex gap-1'>
                          {Array.from({ length: 5 }, (_, i) => (
                            <div
                              key={i}
                              className={cn(
                                'w-1.5 h-1.5 rounded-full',
                                i < getLevelDots(skill.level)
                                  ? 'bg-primary'
                                  : 'bg-muted'
                              )}
                            />
                          ))}
                        </div>
                      </div>
                      <Badge
                        variant='outline'
                        className={cn(
                          'text-xs border',
                          getLevelColor(skill.level)
                        )}
                      >
                        {skill.level}
                      </Badge>
                    </div>
                  ))}
                </CardContent>
              </Card>
            );
          })}
        </div>

        {/* Methodologies */}
        <div className='mt-16 text-center'>
          <h3 className='text-2xl font-semibold text-foreground mb-6'>
            Development Methodologies
          </h3>
          <div className='flex flex-wrap justify-center gap-3'>
            {[
              'Agile/Scrum',
              'Test-Driven Development',
              'Clean Architecture',
              'Microservices',
              'Domain-Driven Design',
              'DevOps Culture',
            ].map(methodology => (
              <Badge
                key={methodology}
                variant='outline'
                className='text-sm px-4 py-2'
              >
                {methodology}
              </Badge>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default SkillsSection;
